import { ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import {
    FiChecklistHelperService,
    IChecklistCheckpointPlaceholderLabelData,
} from '@fi-shared/checklist/fi-checklist-helper.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { AppTextService } from '@fi-shared/app-text';
import { IChecklistV2 } from '@fi-shared/checklist/checklist-v2';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { ChecklistCreationTypes } from '../../checklist-enum';
import { IAddChecklistDialogResult, IChecklistCreationTypeOption } from '@fi-shared/checklist/checklist';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { isDefined } from '@fi-shared/utils';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { ObjectTypes } from '@fi-shared/types';
import { IFiDialogOptions } from '@fi-shared/fi-dialogs/fi-dialogs';
import { Subject } from 'rxjs';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';

interface IChecklistAddChecklistDialogData {
    lightWeightChecklistTemplates: INameIdObj[];
    auditMode: boolean;
}

@Component({
    selector: 'add-checklist-dialog',
    templateUrl: './add-checklist-dialog.component.html',
    styleUrl: '../../fi-checklist2-template/add-checklist-template-dialog/add-checklist-template-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist-add-checklist-dialog' },
    standalone: false,
})
export class FiChecklistAddChecklistDialogComponent
    extends FiDialogsContentBase<IChecklistAddChecklistDialogData, IAddChecklistDialogResult>
    implements OnInit, OnDestroy
{
    checklist: IChecklistV2;
    checklistPlaceholderLabelData: IChecklistCheckpointPlaceholderLabelData;
    currentChecklistCreationType: ChecklistCreationTypes;
    checklistCreationTypeOptions: IChecklistCreationTypeOption[] = [];
    selectedTemplateId: number;
    private fiValuesComparison: FiValuesComparison<IChecklistV2>;

    protected readonly ObjectTypes = ObjectTypes;

    get lightWeightChecklistTemplates(): INameIdObj[] {
        return this.data.lightWeightChecklistTemplates;
    }
    private get auditMode(): boolean {
        return this.data.auditMode;
    }

    protected elementRef = inject(ElementRef);

    ngOnInit() {
        /*// todo for tests
        this.currentChecklistCreationType = ChecklistCreationTypes.UploadFromExcel;*/

        this.setServices();
        this.setChecklistCreationTypeOptions();
        this.setInitialChecklistCommonData();
        this.setChecklistLabels();
        this.fiValuesComparison = new FiValuesComparison(() => this.checklist);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        const dialogData: IFiDialogOptions<null, null> =
            this.fiChecklistHelperService.getChecklistDiscardChangesDialogData(
                this.checklist.Id,
                ObjectTypes.Checklist2,
            );
        const canCloseCondition: boolean = !isDefined(this.selectedTemplateId) && !this.fiValuesComparison.isChanged();

        return this.fiDialogsService.getCanCloseConfirmation(canCloseCondition, dialogData);
    }

    private setChecklistCreationTypeOptions() {
        this.checklistCreationTypeOptions = [
            {
                type: ChecklistCreationTypes.Manual,
                label: this.appTextService.get('checklist.checklist_create_manually'),
                description: this.appTextService.get('checklist.checklist_create_manually_description'),
            },
            {
                type: ChecklistCreationTypes.Template,
                label: this.appTextService.get('checklist.checklist_create_using_template'),
                description: this.appTextService.get('checklist.checklist_create_using_template_description'),
            },
            {
                type: ChecklistCreationTypes.UploadFromExcel,
                label: this.appTextService.get('common.upload_from_excel'),
                description: this.appTextService.get('checklist.checklist_upload_from_excel_description', {
                    '{0}': this.appTextService.get('common.checklist').toLowerCase(),
                }),
            },
        ];
    }

    private setInitialChecklistCommonData() {
        this.checklist = this.fiChecklistHelperService.getChecklistInstanceV2();
    }

    private setChecklistLabels() {
        this.checklistPlaceholderLabelData = this.fiChecklistHelperService.checklistPlaceholderLabelData;
    }

    onCurrentChecklistCreationTypeChange() {
        this.selectedTemplateId = undefined;
        this.setInitialChecklistCommonData();
    }

    getNewChecklistHeader(): string {
        return this.appTextService.get(
            !this.auditMode ? 'checklist.create_new_checklist' : 'checklist.create_new_audit_checklist',
        );
    }

    isYesButtonDisabled(): boolean {
        if (this.isChecklistCreationTypeSelected(ChecklistCreationTypes.Template)) {
            return !this.selectedTemplateId;
        }

        if (this.isChecklistCreationTypeSelected(ChecklistCreationTypes.UploadFromExcel)) {
            return !this.checklist.Names.find((item) => item.Value)?.Value;
        }

        return !this.checklist.Names.find((name: IContentLanguageLabelModel) => {
            return name.ContentLanguageId === this.getUserPreferredContentLanguage();
        })?.Value;
    }

    getUserPreferredContentLanguage(): number {
        const preferredContentLanguage = this.fiUserDataService.getContentLanguage();
        return preferredContentLanguage;
    }

    isChecklistCreationTypeSelected(creationType: ChecklistCreationTypes): boolean {
        return this.currentChecklistCreationType === creationType;
    }

    protected onChecklistImported($event: IChecklistV2) {
        this.checklist = $event;
        if (!this.checklist) {
            this.setInitialChecklistCommonData();
        }
    }

    getResult(): IAddChecklistDialogResult {
        return {
            type: this.currentChecklistCreationType,
            checklist: this.checklist,
            templateId: this.selectedTemplateId,
        };
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly ChecklistCreationTypes = ChecklistCreationTypes;

    private fiChecklistHelperService: FiChecklistHelperService;
    private fiUserDataService: FiUserDataService;
    private appTextService: AppTextService;
    private fiDialogsService: FiDialogsService;
    private fiCustomerDataService: FiCustomerDataService;
    private setServices() {
        this.fiChecklistHelperService = this.injector.get(FiChecklistHelperService);
        this.fiUserDataService = this.injector.get(FiUserDataService);
        this.fiCustomerDataService = this.injector.get(FiCustomerDataService);
        this.appTextService = this.injector.get(AppTextService);
        this.fiDialogsService = this.injector.get(FiDialogsService);
    }
}
